import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import {
  AppBar,
  Toolbar,
  Typography,
  Tabs,
  Tab,
  Drawer,
  IconButton,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useReportContext } from "../hooks/useReportContext";

const StatusBarWrapper = styled(Box)({
  position: "relative",
  zIndex: 20,
});

const StyledAppBar = styled(AppBar)({
  position: "relative",
  backgroundColor: "#f5f5f5",
  zIndex: 20,
  borderTop: "1px solid #ddd",
  borderBottom: "1px solid #ddd",
  width: "100%",
  boxShadow: "0px -2px 4px rgba(0, 0, 0, 0.05)",
});

const StyledToolbar = styled(Toolbar)({
  minHeight: "40px",
  padding: "0 16px",
});

const StatusText = styled(Typography)({
  flexGrow: 1,
  textTransform: "uppercase",
  fontSize: "0.875rem",
  fontWeight: 500,
});

const StyledTabs = styled(Tabs)({
  minHeight: "40px",
  "& .MuiTab-root": {
    minHeight: "40px",
    padding: "0 16px",
  },
});

const Overlay = styled(Box)({
  position: "absolute",
  top: "-1000px",
  left: 0,
  right: 0,
  bottom: "40px",
  backgroundColor: "rgba(0, 0, 0, 0.3)",
  zIndex: 10,
  pointerEvents: "auto",
});

const StyledDrawer = styled(Drawer)({
  position: "absolute",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 15,
  pointerEvents: "none",
  "& .MuiDrawer-paper": {
    pointerEvents: "auto",
    position: "absolute",
    bottom: "40px",
    left: 0,
    right: 0,
    maxHeight: "calc(100vh - 200px)",
    borderRadius: "8px 8px 0 0",
    boxShadow: "0px -4px 8px rgba(0, 0, 0, 0.1)",
    transform: "none",
    zIndex: 15,
  },
});

const LogsTableContainer = styled(Box)({
  margin: "20px",
  position: "relative",
  "& .MuiTableHead-root": {
    position: "sticky",
    top: 0,
    zIndex: 100,
    backgroundColor: "#fff !important",
  },
  "& .MuiTable-root": {
    borderCollapse: "separate",
  },
  "& .MuiTableCell-head": {
    backgroundColor: "#fff !important",
    borderBottom: "2px solid #e0e0e0 !important",
    position: "sticky !important",
    top: 0,
    zIndex: 100,
    padding: "1px 16px !important",
  },
  "& .MuiTableRow-head": {
    backgroundColor: "#fff !important",
  },
  "& .MuiTypography-subtitle1": {
    border: "none !important",
    outline: "none !important",
    boxShadow: "none !important",
    backgroundColor: "transparent !important",
  },
});

const LogsLoadingOverlay = styled(Box)({
  position: "absolute",
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  background: "rgba(245, 245, 245, 0.5)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
});

const StyledTableContainer = styled(TableContainer)({
  maxHeight: 600,
  overflow: "auto",
});

const HeaderCell = styled(Typography)({
  fontWeight: "bold",
  position: "sticky !important",
  top: 0,
  backgroundColor: "#fff !important",
  zIndex: 100,
  borderBottom: "2px solid #e0e0e0 !important",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
  padding: "8px 16px !important",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "#fff",
    zIndex: -1,
  },
});

const TableHeader = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  width: "100%",
  padding: "0",
  backgroundColor: "#fff",
});

const HeaderActions = styled(Box)({
  display: "flex",
  alignItems: "center",
});

const RefreshButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

// Logs table component
const LogsTable = ({ logs, handleDrawerClose, isLogsLoading, onRefresh }) => {
  return (
    <LogsTableContainer>
      {isLogsLoading && (
        <LogsLoadingOverlay>
          <CircularProgress />
        </LogsLoadingOverlay>
      )}
      <StyledTableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableHeader>
                  <HeaderCell variant="subtitle1">Logs</HeaderCell>
                  <HeaderActions>
                    <RefreshButton
                      onClick={onRefresh}
                      title="Refresh logs"
                      size="small"
                    >
                      <RefreshIcon fontSize="small" />
                    </RefreshButton>
                    <IconButton
                      onClick={handleDrawerClose}
                      title="Close logs"
                      size="small"
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </HeaderActions>
                </TableHeader>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {logs?.map((log, index) => (
              <TableRow key={index}>
                <TableCell component="th" scope="row">
                  {log}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </StyledTableContainer>
    </LogsTableContainer>
  );
};

const StatusBar = () => {
  const [logsDrawerOpen, setLogsDrawerOpen] = useState(false);
  const hasInitiallyFetchedRef = useRef(false);

  // Use report context instead of props
  const {
    reportRunData,
    logsData,
    logsLoading,
    refetchReportLogs,
    isReportLogsAvailable,
    executionId,
  } = useReportContext();

  // Handle tab click to open logs drawer and fetch logs only once when opening
  const handleLogsTabClick = () => {
    if (isReportLogsAvailable()) {
      // Fetch logs once when drawer is opened
      if (!logsDrawerOpen && executionId) {
        refetchReportLogs(executionId);
        hasInitiallyFetchedRef.current = true;
      }
      setLogsDrawerOpen(true);
    }
  };

  // Reset the fetched flag when drawer closes
  useEffect(() => {
    if (!logsDrawerOpen) {
      hasInitiallyFetchedRef.current = false;
    }
  }, [logsDrawerOpen]);

  // Handle drawer close
  const handleDrawerClose = () => {
    setLogsDrawerOpen(false);
  };

  const handleRefreshLogs = () => {
    if (executionId) {
      refetchReportLogs(executionId);
    }
  };

  return (
    <StatusBarWrapper className="status-bar-wrapper">
      {/* Overlay to block grid when drawer is open */}
      {logsDrawerOpen && <Overlay onClick={handleDrawerClose} />}

      <StyledAppBar position="static" elevation={3}>
        <StyledToolbar>
          <StatusText>{reportRunData?.statusText || ""}</StatusText>
          <StyledTabs value={false} onClick={handleLogsTabClick}>
            <Tab label="Logs" disabled={!isReportLogsAvailable()} />
          </StyledTabs>
        </StyledToolbar>
      </StyledAppBar>

      {/* Logs Drawer */}
      <StyledDrawer
        anchor="bottom"
        open={logsDrawerOpen}
        onClose={handleDrawerClose}
        variant="persistent"
        ModalProps={{
          container: () =>
            document.querySelector(".status-bar-wrapper") || document.body,
          disableEnforceFocus: true,
          hideBackdrop: true,
        }}
      >
        <LogsTable
          logs={logsData?.logs || []}
          handleDrawerClose={handleDrawerClose}
          isLogsLoading={logsLoading}
          onRefresh={handleRefreshLogs}
        />
      </StyledDrawer>
    </StatusBarWrapper>
  );
};

export default StatusBar;
